// @ts-strict-ignore
import { ArticleContainer } from '@3d-scene/model3d/article-container';
import { TableProviderGenerator } from '../label-editor/service/table-provider-generator';
import { TableProviderPersister } from '../label-editor/service/table-provider-persister';
import { OpenLabelEditorService } from '../label-editor/service/open-label-editor.service';
import { Connection } from '@3d-scene/model3d/connection';
import { TextSizeCheckManager } from '@3d-scene/manager/text-size-check.manager';
import { ReplaceLabelingDataBuilder } from './replace-labeling-data-builder';
import { LabelingUtils } from '@3d-scene/model3d/util/labeling-utils';
import { TextSizeCheckRequestVO } from '@3d-scene/manager/text-size-check.vo';
import { ArrayUtils } from '@shared/util/array-utils';
import { Injectable } from '@angular/core';

export class LabelingTextCheckCorrectionResult {
  private readonly _invalidTextCheckContainers: ArticleContainer[] = [];
  private readonly _textureConnectionsToRefresh: Connection[] = [];

  public get invalidTextCheckContainers(): ArticleContainer[] {
    return this._invalidTextCheckContainers;
  }

  public addInvalidTextCheckContainer(container: ArticleContainer): void {
    this._invalidTextCheckContainers.push(container);
  }

  public hasInvalidTextCheckContainers(): boolean {
    return this._invalidTextCheckContainers.length > 0;
  }

  public get textureConnectionsToRefresh(): Connection[] {
    return this._textureConnectionsToRefresh;
  }

  public addTextureConnectionsToRefresh(connections: Connection[]): void {
    this._textureConnectionsToRefresh.push(...connections);
  }
}

@Injectable({ providedIn: 'root' })
export class ReplaceLabelingTextCheckCorrector {
  constructor(
    private tableProviderGenerator: TableProviderGenerator,
    private tableProviderPersister: TableProviderPersister,
    private labelEditorService: OpenLabelEditorService,
    private textSizeCheckManager: TextSizeCheckManager,
    private labelingUtils: LabelingUtils
  ) {}

  public showLabelEditor(labelingContainers: ArticleContainer[]): void {
    this.labelEditorService.execute(this.extractTextConnections(labelingContainers), false, false, false);
  }

  public async correctTextSizeChecks(labelingContainers: ArticleContainer[]): Promise<LabelingTextCheckCorrectionResult> {
    await this.retrieveTextSizeCheckInfo(labelingContainers);
    return this.correctTextSizeChecksForLabelings(labelingContainers);
  }

  private async retrieveTextSizeCheckInfo(labelingContainers: ArticleContainer[]): Promise<boolean> {
    const cubesToBuildRequest: Connection[] = [];
    for (const container of labelingContainers) {
      const textConnections: Connection[] = this.extractTextConnectionsFromContainer(container);
      if (textConnections.length > 0) {
        cubesToBuildRequest.push(textConnections[0]);
      }
    }

    const commonTextSizeCheckRequests: TextSizeCheckRequestVO[] = this.textSizeCheckManager.buildCommonTextSizeCheckRequest(cubesToBuildRequest);
    if (ArrayUtils.isEmpty(commonTextSizeCheckRequests)) {
      return true;
    }
    return this.textSizeCheckManager.retrieveTextSizeCheckInfos(commonTextSizeCheckRequests);
  }

  private correctTextSizeChecksForLabelings(labelingContainers: ArticleContainer[]): LabelingTextCheckCorrectionResult {
    const result = new LabelingTextCheckCorrectionResult();
    for (const container of labelingContainers) {
      const textureConnections = this.extractTextConnectionsFromContainer(container);
      result.addTextureConnectionsToRefresh(this.labelingUtils.findMergedConnections(textureConnections));
      if (!this.correctTextSizeCheckForLabeling(textureConnections)) {
        result.addInvalidTextCheckContainer(container);
      }
    }
    result.textureConnectionsToRefresh.forEach((connection) => connection.resetTexture());
    return result;
  }

  private correctTextSizeCheckForLabeling(textureConnections: Connection[]): boolean {
    const tableProvider = this.tableProviderGenerator.generate(textureConnections);
    const rowCount = tableProvider.rowCount;
    const colCount = tableProvider.colCount;

    let isValid: boolean = true;
    for (let row: number = 0; row < rowCount; row++) {
      for (let col: number = 0; col < colCount; col++) {
        const spanData = tableProvider.getSpanData(row, col);
        if (!spanData.parent && !spanData.isEndPlate && spanData.autoFormatByTextCheck) {
          spanData.initHtml5Data();
          isValid = !spanData.containsErrors && isValid;
        }
      }
    }
    this.tableProviderPersister.apply(tableProvider);
    return isValid;
  }

  private extractTextConnections(articleContainers: ArticleContainer[]): Connection[] {
    const textConnections: Connection[] = [];
    for (const container of articleContainers) {
      textConnections.push(...this.extractTextConnectionsFromContainer(container));
    }
    return textConnections;
  }

  private extractTextConnectionsFromContainer(container: ArticleContainer): Connection[] {
    return ReplaceLabelingDataBuilder.extractTextureConnections(container).filter(
      (connection) => !this.labelingUtils.getPreviousMergedConnectionId(connection)
    );
  }
}
